import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useGetConfiguration } from '@/hooks/useConfiguration';
import { useFlag, useUpdateFlags } from '@/hooks/useFlags';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { SubscriptionConfiguration, UniqueQueryKeys } from '@/types';
import { useMergeStatus, useMutation } from '@/utils/api';
import { getAttackSurfaceFlagUpdates } from '@/utils/attackSurfaceFlags';

export function useGetSubscription(options?: { email?: string }) {
  // Removed disableCache to leverage React Query's caching
  // Configuration data rarely changes and benefits from caching
  // This improves performance and prevents unnecessary API calls
  const { data: configData, status: configStatus } = useGetConfiguration({
    email: options?.email,
  });

  const {
    enableBreachAndAttack,
    enableCloud,
    enableUsers,
    enableDevice,
    enableExternal,
    enableCode,
    enableLlm,
    isFlagLoading,
  } = useFlag({
    disableCache: true,
    email: options?.email,
    doNotImpersonate: !!options?.email,
  });

  // Convert boolean loading state to proper QueryStatus
  const flagsStatus = isFlagLoading ? 'pending' : 'success';

  const burpModule = configData?.find(c => c.name === 'enable_burp_module');
  const currentSubscription = configData?.find(s => s.name === 'subscription')
    ?.value as
    | Exclude<SubscriptionConfiguration, 'attackSurface' | 'noOfApplications'>
    | undefined;
  const numberOfApplications = Number(burpModule?.value || '0');

  const attackSurface = {
    external: enableExternal,
    applications: numberOfApplications > 0,
    internal: enableBreachAndAttack,
    cloud: enableCloud,
    code: enableCode,
    users: enableUsers,
    device: enableDevice,
    llm: enableLlm,
  };

  return {
    status: useMergeStatus(flagsStatus, configStatus),
    data: currentSubscription
      ? ({
          ...currentSubscription,
          attackSurface,
          numberOfApplications,
        } as SubscriptionConfiguration)
      : undefined,
  };
}

export interface UpdateSubscriptionRequest {
  subscription: SubscriptionConfiguration;
  email?: string;
  silent?: boolean;
}

export type UpdateSubscriptionVariables =
  | SubscriptionConfiguration
  | UpdateSubscriptionRequest;

const isUpdateSubscriptionRequest = (
  value: UpdateSubscriptionVariables
): value is UpdateSubscriptionRequest => 'subscription' in value;

export function useUpdateSubscription(options?: { email?: string }) {
  const axios = useAxios();
  const axiosNotImpersonated = useAxios({ doNotImpersonate: true });
  const updateFlags = useUpdateFlags({ email: options?.email });

  return useMutation<void, Error, UpdateSubscriptionVariables>({
    defaultErrorMessage: 'Failed to update setting',
    mutationFn: async variables => {
      const {
        subscription,
        email: overrideEmail,
        silent,
      } = isUpdateSubscriptionRequest(variables)
        ? variables
        : { subscription: variables, email: undefined, silent: false };
      const targetEmail = overrideEmail ?? options?.email;
      const scopedAxios = targetEmail ? axiosNotImpersonated : axios;
      const requestConfig = targetEmail
        ? { headers: { account: targetEmail } }
        : undefined;

      const { attackSurface, numberOfApplications, ...rest } = subscription;
      const { flagsToAdd, flagsToDelete } =
        getAttackSurfaceFlagUpdates(attackSurface);

      const flagsToUpdate = updateFlags.mutateAsync({
        silent: true,
        flagsToAdd,
        flagsToDelete,
        email: targetEmail,
      });

      const configurationPromise = scopedAxios.put(
        '/configuration',
        {
          name: 'enable_burp_module',
          value: attackSurface['applications'] ? numberOfApplications : 0,
        },
        requestConfig
      );

      const subscriptionPromise = scopedAxios.put(
        '/configuration',
        {
          name: 'subscription',
          value: rest,
        },
        requestConfig
      );

      const promises = Promise.all([
        subscriptionPromise,
        flagsToUpdate,
        configurationPromise,
      ]);

      if (!silent) {
        toast.promise(promises, {
          loading: 'Updating subscription',
          success: 'Subscription updated successfully',
          error: 'Failed to update subscription',
        });
      }

      await promises;

      // Invalidate all relevant caches to ensure UI updates
      // We need to invalidate both general and customer-specific caches

      // Always invalidate the base configuration and flag caches
      // This ensures the settings page updates when changes are made
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('configuration'),
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('flag'),
      });

      // If updating a specific customer, also invalidate their specific caches
      if (targetEmail) {
        // Customer-specific configuration
        queryClient.invalidateQueries({
          queryKey: [...getQueryKey.getMy('configuration'), targetEmail],
        });

        // Customer-specific flags
        queryClient.invalidateQueries({
          queryKey: [...getQueryKey.getMy('flag'), targetEmail],
        });

        // Also invalidate all "my" queries containing the customer email
        // This ensures any impersonation-based queries are also refreshed
        queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey.includes('my') && queryKey.includes(targetEmail);
          },
        });

        // Invalidate monitoring statistics queries for the customer
        queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return (
              queryKey.includes(UniqueQueryKeys.GENERIC_MY_SEARCH) &&
              queryKey.includes(targetEmail)
            );
          },
        });
      } else {
        // If no email provided (updating from settings page),
        // we should still invalidate any cached customer queries
        // This ensures customer drawer updates when admin changes their own settings
        queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            // Invalidate all configuration queries that have an email parameter
            return (
              (queryKey.includes('configuration') ||
                queryKey.includes('flag')) &&
              queryKey.length > 2
            ); // Has additional parameters beyond ['my', 'resource']
          },
        });
      }
    },
  });
}

export function useDeleteSubscription() {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to update setting',
    mutationFn: async () => {
      const promise = axios.delete('/setting', {
        data: {
          name: 'subscription',
        },
      });
      toast.promise(promise, {
        loading: 'Deleting subscription',
        success: 'Subscription deleted successfully',
        error: 'Failed to delete subscription',
      });

      await promise;
      queryClient.invalidateQueries({ queryKey: getQueryKey.getMy('setting') });
    },
  });
}
